import { useRef, useState } from "react";

import { copyTextToSystemClipboard } from "@excalidraw/excalidraw/clipboard";
import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import { FilledButton } from "@excalidraw/excalidraw/components/FilledButton";
import { TextField } from "@excalidraw/excalidraw/components/TextField";
import { copyIcon } from "@excalidraw/excalidraw/components/icons";
import { useCopyStatus } from "@excalidraw/excalidraw/hooks/useCopiedIndicator";
import { useI18n } from "@excalidraw/excalidraw/i18n";
import "@excalidraw/excalidraw/components/ShareableLinkDialog.scss";

import { useDrawolaT } from "../storage/i18n";

export type DrawolaShareLinkDialogProps = {
  link: string;
  /** When the share stops working, epoch ms. The expiry line is hidden when missing. */
  expiresAt?: number;

  onCloseRequest: () => void;
  setErrorMessage: (error: string) => void;
};

/**
 * Fork-owned copy of upstream `ShareableLinkDialog` with one extra line: when
 * the link expires. Upstream takes no slot for extra text and lives in
 * packages/, which this fork must never edit. The notice has to be visible at
 * the moment the user decides whether to send the link, so a toast that
 * disappears is not enough.
 */
export const DrawolaShareLinkDialog = ({
  link,
  expiresAt,
  onCloseRequest,
  setErrorMessage,
}: DrawolaShareLinkDialogProps) => {
  const { t } = useI18n();
  const drawolaT = useDrawolaT();
  const [, setJustCopied] = useState(false);
  const timerRef = useRef<number>(0);
  const ref = useRef<HTMLInputElement>(null);

  const copyRoomLink = async () => {
    try {
      await copyTextToSystemClipboard(link);
    } catch (e) {
      setErrorMessage(t("errors.copyToSystemClipboardFailed"));
    }
    setJustCopied(true);

    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
    }

    timerRef.current = window.setTimeout(() => {
      setJustCopied(false);
    }, 3000);

    ref.current?.select();
  };
  const { onCopy, copyStatus } = useCopyStatus();
  return (
    <Dialog onCloseRequest={onCloseRequest} title={false} size="small">
      <div className="ShareableLinkDialog">
        <h3>{drawolaT("share.title")}</h3>
        <div className="ShareableLinkDialog__linkRow">
          <TextField
            ref={ref}
            label={drawolaT("share.linkLabel")}
            readonly
            fullWidth
            value={link}
            selectOnRender
          />
          <FilledButton
            size="large"
            label={t("buttons.copyLink")}
            icon={copyIcon}
            status={copyStatus}
            onClick={() => {
              onCopy();
              copyRoomLink();
            }}
          />
        </div>
        <div className="ShareableLinkDialog__description">
          🔒 {t("alerts.uploadedSecurly")}
        </div>
        {typeof expiresAt === "number" && (
          <div className="ShareableLinkDialog__description">
            {drawolaT("share.expiresOn", {
              date: new Date(expiresAt).toLocaleDateString(),
            })}
          </div>
        )}
      </div>
    </Dialog>
  );
};
